import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import Container from "../Container/Container"
import PreconfirmationStep from "./PreconfirmationStep/PreconfirmationStep"
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle"
import { Typography } from "@allaria-tech/matter/Typography"
import {
  Preconfirmation,
  PreconfirmationStep as PreconfirmationStepType,
} from "../../types"
import { RootState } from "../../app/store"
import { Tabs } from "@allaria-tech/matter/Tabs"
import useWindowWidth from "../../hooks/useWindowWidth"

import styles from "./PreconfirmationCard.module.scss"

const Header = ({
  headerContent,
  isMobile,
}: {
  headerContent: Preconfirmation["headerContent"]
  isMobile: boolean
}) => (
  <div className={styles.header}>
    {headerContent ? (
      headerContent
    ) : (
      <>
        <Typography type="HEADING" size={isMobile ? "M" : "L"} tag="h1">
          Preconfirmación de apertura
        </Typography>
        <Typography type="BODY" size="S">
          Revisá que los datos sean correctos.
        </Typography>
      </>
    )}
  </div>
)

const Steps = ({
  steps,
  isLoading,
}: {
  steps: PreconfirmationStepType[]
  isLoading: boolean
}) =>
  isLoading ? (
    <div className={styles.loading}>
      <LoadingCircle />
    </div>
  ) : (
    <div className={`${styles.stepsContainer} scrollbar`}>
      {steps
        ?.filter((step) => !step.hideStep)
        .map((step) => (
          <PreconfirmationStep key={step.name} {...step} />
        ))}
    </div>
  )

const PreconfirmationCard = ({
  preconfirmationSteps,
}: {
  preconfirmationSteps: PreconfirmationStepType[]
}) => {
  const { container, content, tabbedContainer } = styles

  const isMobile = useWindowWidth().width < 768

  const state = useAppSelector((state: RootState) => state)
  const { onboardingData, securities, activeTemplate } = state

  const { preconfirmation, onboardingType } = activeTemplate

  const { formValues } =
    onboardingType === "SECURITIES" ? securities : onboardingData

  const { headerContent, tabs, onChangeTab, disabledKeys, defaultSelectedKey } =
    preconfirmation
      ? preconfirmation({ values: formValues })
      : ({} as Preconfirmation)

  const dispatch = useAppDispatch()

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (onboardingType && formValues) setIsLoading(false)
  }, [onboardingType, formValues])

  const disabledTabKeys = disabledKeys
    ? disabledKeys({ state, dispatch })
    : []

  return tabs?.length ? (
    <div className={tabbedContainer}>
      <Tabs
        onChange={(key) =>
          onChangeTab ? onChangeTab({ key, state, dispatch }) : undefined
        }
        defaultActiveKey={
          defaultSelectedKey
            ? defaultSelectedKey({ state, dispatch })
            : undefined
        }
        items={tabs.map(({ label, steps: tabSteps }, i) => ({
          key: i.toString(),
          label,
          disabled: disabledTabKeys.includes(i.toString()),
          content: (
            <Container key={i} className={content}>
              <Header headerContent={headerContent} isMobile={isMobile} />
              <Steps steps={tabSteps} isLoading={isLoading} />
            </Container>
          ),
        }))}
      />
    </div>
  ) : (
    <Container className={`${container} ${content}`}>
      <Header headerContent={headerContent} isMobile={isMobile} />
      <Steps steps={preconfirmationSteps} isLoading={isLoading} />
    </Container>
  )
}

export default PreconfirmationCard
